/**
 * Create & Interpret Box Plots generator (6.DPSR.1.2).
 *
 * A whole-number data set is generated, its five-number summary is computed
 * with the exact statistics engine, and the student reads one value off the
 * summary. A renderer-agnostic BoxPlotModel is attached so the same summary can
 * be drawn as a box-and-whisker plot. The whole summary is validated to be
 * integer-valued before emitting, so every asked value is a clean whole number.
 *
 * Families:
 *   F1: find the median (Q2)   (number)
 *   F2: find Q1 (lower quartile) (number)
 *   F3: find Q3 (upper quartile) (number)
 *   F4: find the IQR (Q3 - Q1)   (number)
 */

import { rat, type Rational } from "../engine/rational";
import { Rng } from "../engine/rng";
import { getTopic } from "../curriculum/curriculum";
import { sortAscending, fiveNumberSummary, iqr } from "../engine/statistics";
import type { BoxPlotModel } from "../graph/plot-model";
import type {
  Difficulty,
  GenerateContext,
  Generator,
  Question,
  QuestionSignature,
} from "./types";

const TOPIC = "box-plots" as const;
const VERSION = "1.0.0";

type Family = "F1" | "F2" | "F3" | "F4";

interface Cfg {
  readonly families: readonly Family[];
  /** Odd sizes with odd half-sizes so quartiles land on whole numbers. */
  readonly sizes: readonly number[];
  readonly valueMax: number;
}

const DIFF: Record<Difficulty, Cfg> = {
  easy: { families: ["F1", "F2", "F3"], sizes: [7], valueMax: 20 },
  standard: { families: ["F1", "F2", "F3", "F4"], sizes: [7, 11], valueMax: 40 },
  hard: { families: ["F1", "F2", "F3", "F4"], sizes: [7, 11], valueMax: 60 },
  challenge: { families: ["F2", "F3", "F4"], sizes: [11], valueMax: 100 },
};

export class BoxPlotsGenerator implements Generator {
  readonly topic = TOPIC;
  readonly version = VERSION;
  readonly supportedDifficulties: readonly Difficulty[] = [
    "easy",
    "standard",
    "hard",
    "challenge",
  ];

  generate(ctx: GenerateContext): Question {
    const topic = getTopic(TOPIC);
    for (let attempt = 0; attempt < 100; attempt++) {
      const rng = new Rng(`${ctx.seed}#${attempt}`);
      const cfg = DIFF[ctx.difficulty];
      const family = rng.pick(cfg.families);
      const size = rng.pick(cfg.sizes);

      const data: Rational[] = [];
      for (let i = 0; i < size; i++) data.push(rat(rng.int(1, cfg.valueMax)));
      const sorted = sortAscending(data);
      const summary = fiveNumberSummary(sorted);

      // All five summary values must be whole numbers so any asked value is clean.
      if (
        !summary.min.isInteger() ||
        !summary.q1.isInteger() ||
        !summary.median.isInteger() ||
        !summary.q3.isInteger() ||
        !summary.max.isInteger()
      ) {
        continue;
      }

      let answer: Rational;
      let asked: string;
      let hint1: string;
      let hint2: string;

      switch (family) {
        case "F1": {
          answer = summary.median;
          asked = "the median (Q2)";
          hint1 = "The median is the line inside the box.";
          hint2 = "It is the middle value of the data.";
          break;
        }
        case "F2": {
          answer = summary.q1;
          asked = "the lower quartile (Q1)";
          hint1 = "Q1 is the left edge of the box.";
          hint2 = "It is the median of the lower half of the data.";
          break;
        }
        case "F3": {
          answer = summary.q3;
          asked = "the upper quartile (Q3)";
          hint1 = "Q3 is the right edge of the box.";
          hint2 = "It is the median of the upper half of the data.";
          break;
        }
        case "F4": {
          answer = iqr(summary);
          asked = "the interquartile range (IQR)";
          hint1 = "The IQR is the width of the box, Q3 - Q1.";
          hint2 = "Subtract the left edge (Q1) from the right edge (Q3).";
          break;
        }
      }

      const graph: BoxPlotModel = {
        kind: "box-plot",
        min: summary.min.toNumber(),
        q1: summary.q1.toNumber(),
        median: summary.median.toNumber(),
        q3: summary.q3.toNumber(),
        max: summary.max.toNumber(),
      };

      return {
        id: `${TOPIC}-${ctx.seed}-${family}`,
        generator: TOPIC,
        generatorVersion: VERSION,
        seed: ctx.seed,
        unit: topic.unit,
        topic: TOPIC,
        family,
        difficulty: ctx.difficulty,
        prompt: {
          text: `The box plot summarizes a data set with minimum ${summary.min.toString()}, Q1 ${summary.q1.toString()}, median ${summary.median.toString()}, Q3 ${summary.q3.toString()}, and maximum ${summary.max.toString()}. Find ${asked}.`,
        },
        answerType: "number",
        answer: { kind: "number", value: answer },
        steps: [
          {
            description: `Read ${asked} from the five-number summary.`,
            latex: answer.toLatex(),
          },
        ],
        hints: [
          { level: 1, text: hint1 },
          { level: 2, text: hint2 },
        ],
        subskills: topic.subskills,
        graph,
      };
    }
    throw new Error(`${TOPIC}: failed after 100 attempts (${ctx.seed}, ${ctx.difficulty})`);
  }

  signature(q: Question): QuestionSignature {
    const structure = q.answer.kind === "number" ? q.answer.value.key() : q.answer.kind;
    return { generator: TOPIC, family: q.family, difficulty: q.difficulty, structure };
  }
}

export const boxPlots = new BoxPlotsGenerator();
